// kumioOS proof of work.
//
// A share is a 64-bit nonce such that SHA-256(seed || nonce) starts with at
// least `bits` zero bits. The message is exactly 40 bytes:
//
//   seed[32]  — issued by the coordinator, bound to one recipient + challenge
//   nonce[8]  — big-endian, chosen by the miner (hi word, then lo word)
//
// A 40-byte message fits a single SHA-256 block, so the padding is constant:
// 0x80 at byte 40 and the bit length (320) in the last word. The first eight
// message words never change during a challenge, so rounds 0..7 can be
// precomputed once (the "midstate") and every nonce only pays for rounds 8..63.
//
// The coordinator verifies with node:crypto; this file is the reference for
// the browser CPU workers and the WGSL kernel, and the tests cross-check them.

export const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

export const IV = new Uint32Array([
  0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
]);

/** Difficulty bounds every deployment enforces regardless of configuration. */
export const MIN_BITS_FLOOR = 16;
export const MAX_BITS_CEIL = 52;

export const isHex = (s: string, bytes: number) => typeof s === 'string' && s.length === bytes * 2 && /^[0-9a-f]+$/.test(s);

export function seedWords(seedHex: string): Uint32Array {
  if (!isHex(seedHex, 32)) throw new Error('seed must be 32 bytes of lowercase hex');
  const w = new Uint32Array(8);
  for (let i = 0; i < 8; i++) w[i] = parseInt(seedHex.slice(i * 8, i * 8 + 8), 16) >>> 0;
  return w;
}

const rotr = (x: number, n: number) => (x >>> n) | (x << (32 - n));

/**
 * State after rounds 0..7 (which only touch the seed words), plus the partial
 * message-schedule terms that do not depend on the nonce.
 */
export interface Midstate {
  seed: Uint32Array;
  state: Uint32Array; // a..h after round 7
}

export function midstate(seed: Uint32Array): Midstate {
  let a = IV[0], b = IV[1], c = IV[2], d = IV[3], e = IV[4], f = IV[5], g = IV[6], h = IV[7];
  for (let i = 0; i < 8; i++) {
    const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
    const ch = (e & f) ^ (~e & g);
    const t1 = (h + S1 + ch + K[i] + seed[i]) | 0;
    const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
    const mj = (a & b) ^ (a & c) ^ (b & c);
    const t2 = (S0 + mj) | 0;
    h = g; g = f; f = e; e = (d + t1) | 0; d = c; c = b; b = a; a = (t1 + t2) | 0;
  }
  return { seed, state: Uint32Array.from([a, b, c, d, e, f, g, h]) };
}

const W = new Uint32Array(64);

/** Leading zero bits of SHA-256(seed || hi || lo), using a precomputed midstate. */
export function zerosFrom(ms: Midstate, hi: number, lo: number): number {
  const s = ms.seed;
  for (let i = 0; i < 8; i++) W[i] = s[i];
  W[8] = hi; W[9] = lo; W[10] = 0x80000000;
  W[11] = 0; W[12] = 0; W[13] = 0; W[14] = 0; W[15] = 320;
  for (let i = 16; i < 64; i++) {
    const x = W[i - 15], y = W[i - 2];
    const s0 = rotr(x, 7) ^ rotr(x, 18) ^ (x >>> 3);
    const s1 = rotr(y, 17) ^ rotr(y, 19) ^ (y >>> 10);
    W[i] = (W[i - 16] + s0 + W[i - 7] + s1) | 0;
  }
  const st = ms.state;
  let a = st[0], b = st[1], c = st[2], d = st[3], e = st[4], f = st[5], g = st[6], h = st[7];
  for (let i = 8; i < 64; i++) {
    const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
    const ch = (e & f) ^ (~e & g);
    const t1 = (h + S1 + ch + K[i] + W[i]) | 0;
    const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
    const mj = (a & b) ^ (a & c) ^ (b & c);
    const t2 = (S0 + mj) | 0;
    h = g; g = f; f = e; e = (d + t1) | 0; d = c; c = b; b = a; a = (t1 + t2) | 0;
  }
  const h0 = (a + IV[0]) >>> 0;
  if (h0 !== 0) return Math.clz32(h0);
  return 32 + Math.clz32((b + IV[1]) >>> 0);
}

/** Convenience wrapper without a cached midstate (tests, one-off checks). */
export function zeros(seedHex: string, nonceHex: string): number {
  if (!isHex(nonceHex, 8)) throw new Error('nonce must be 8 bytes of lowercase hex');
  return zerosFrom(midstate(seedWords(seedHex)), parseInt(nonceHex.slice(0, 8), 16) >>> 0, parseInt(nonceHex.slice(8), 16) >>> 0);
}

export const nonceHex = (hi: number, lo: number) =>
  (hi >>> 0).toString(16).padStart(8, '0') + (lo >>> 0).toString(16).padStart(8, '0');

/** Leading zero bits of a digest given as bytes. */
export function leadingZeroBits(bytes: Uint8Array): number {
  let n = 0;
  for (const byte of bytes) {
    if (byte === 0) {
      n += 8;
      continue;
    }
    return n + Math.clz32(byte) - 24;
  }
  return n;
}

/**
 * Difficulty the miner should search at so it finds roughly one share every
 * `targetSeconds` at the measured hash rate, clamped to the challenge bounds.
 */
export function bitsForRate(hashesPerSecond: number, minBits: number, maxBits: number, targetSeconds = 2): number {
  const b = Math.floor(Math.log2(Math.max(1, hashesPerSecond * targetSeconds)));
  return Math.max(minBits, Math.min(maxBits, b));
}
